import { useEffect, useState } from "react";
import { Alert, Badge, Button, Card, Form, Spinner } from "react-bootstrap";
import {
  DeliveryService,
  type EmailAccountState,
  type ProviderAccount,
  type TestEmailResult,
} from "@services/delivery/deliveryService";
import { encodeArtwork } from "@/shared/utils/artworkTransport";

/*
    Sending one real email to an address you type in (MPN-1716).

    Deliberately not a preview. A preview proves our template renders in a browser, which is
    the easy half; this proves SES accepts it, DKIM signs it, the footer is there, and the
    recipient's actual mail client displays it. Those are different questions and only the
    second one predicts what a student sees.

    The sandbox banner is the most useful thing on this card. An SES account that has not been
    granted production access can only email verified addresses, and it reports that as a
    rejection about identities rather than as a state anybody would think to check. Somebody
    hitting it without the banner spends half an hour on the wrong problem.
*/

// The starting point, not the only thing that can be sent. Editable below, because "does
// our sample render" is a question nobody has, and "does the copy I am about to ship render"
// is the only one worth a real send.
const SAMPLE_BODY = "Hi {{first_name}}, registration for spring closes on the 14th.";

// Wrapped at send time rather than edited as raw HTML. Somebody checking a subject line
// should not have to write a paragraph tag, and an unstyled body is the one case where what
// arrives looks nothing like what a campaign would send.
const wrap = (body: string) =>
  '<p style="font-family:Helvetica,Arial,sans-serif;font-size:15px;line-height:1.5">' +
  body.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/\n/g, "<br>") +
  "</p>";

export const EmailTestCard = ({ accounts }: { accounts: ProviderAccount[] }) => {
  const emailAccounts = accounts.filter((a) => a.provider === "ses");
  const [accountId, setAccountId] = useState<number | null>(emailAccounts[0]?.id ?? null);
  const [to, setTo] = useState("");
  // An email ad from the Content Catalog (MPN-2172). The container id in the catalog URL
  // works too: the server finds the email inside it. When set, the ad supplies the subject,
  // preheader and HTML, so the typed fields below are hidden.
  const [adId, setAdId] = useState("");
  const [subject, setSubject] = useState("Registration closes on the 14th");
  const [body, setBody] = useState(SAMPLE_BODY);
  const [senderName, setSenderName] = useState("");
  const [state, setState] = useState<EmailAccountState | null>(null);
  const [results, setResults] = useState<TestEmailResult[]>([]);
  const [sending, setSending] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const service = new DeliveryService();

  useEffect(() => {
    if (!accountId) return;
    let cancelled = false;
    service
      .emailAccountState(accountId)
      .then((s) => !cancelled && setState(s))
      // A state we could not read is not worth an error on this card. The send itself will
      // say what is wrong, and with a better message than a failed GET could.
      .catch(() => !cancelled && setState(null));
    return () => {
      cancelled = true;
    };
  }, [accountId]);

  if (emailAccounts.length === 0) return null;

  // A review usually goes to two or three people, so the box takes a list.
  const recipients = to.split(/[\s,;]+/).filter((a) => a.includes("@"));
  const fromAd = adId.trim() !== "";

  const send = async () => {
    setSending(true);
    setErrors([]);
    setResults([]);
    const sent: TestEmailResult[] = [];
    const failed: string[] = [];
    // One request per address, so one bad address does not stop the others.
    for (const address of recipients) {
      try {
        sent.push(
          await service.testEmail(
            accountId!,
            fromAd
              ? { to: address, ad_id: Number(adId), sender_name: senderName || "your institution" }
              : {
                  to: address,
                  subject,
                  preheader: "Two weeks left to register",
                  // Encoded, because the body is styled HTML and a WAF rule rejects CSS in a
                  // request body before it reaches the API (see artworkTransport).
                  html_b64: encodeArtwork(wrap(body)),
                  // The plain-text alternative, from the same words. An email with no text
                  // part reads as a phish to a spam filter.
                  text: body,
                  sender_name: senderName || "your institution",
                },
          ),
        );
      } catch (e) {
        failed.push(`${address}: ${e instanceof Error ? e.message : "The test email failed."}`);
      }
    }
    setResults(sent);
    setErrors(failed);
    setSending(false);
  };

  return (
    <Card className="mb-4">
      <Card.Header>Send a test email</Card.Header>
      <Card.Body>
        <p className="text-muted small">
          Sends one real email, assembled exactly the way a campaign would assemble it,
          including the footer and a working unsubscribe link. Put in any address you can
          check.
        </p>

        <Alert variant="light" className="py-2 small border">
          Test emails come from Motimatic&apos;s existing verified domain, so this works
          before the marketing sending domain exists. A real campaign uses the client&apos;s
          own From address instead, set on the campaign, and never falls back to ours.
        </Alert>

        {state?.sandbox && (
          <Alert variant="warning" className="py-2">
            <strong>This account is in the SES sandbox.</strong> It can only email addresses
            that have been verified in SES. Verify the address you want to test with, or ask
            for production access before a real campaign.
          </Alert>
        )}

        <div className="d-flex gap-3 align-items-end flex-wrap mb-3">
          {emailAccounts.length > 1 && (
            <Form.Group controlId="email-test-account">
              <Form.Label>Account</Form.Label>
              <Form.Select
                value={accountId ?? ""}
                onChange={(e) => setAccountId(Number(e.target.value))}
              >
                {emailAccounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}
          <Form.Group controlId="email-test-to" style={{ minWidth: 320 }}>
            <Form.Label>Send to</Form.Label>
            <Form.Control
              value={to}
              placeholder="you@example.com, a.colleague@example.com"
              onChange={(e) => setTo(e.target.value)}
            />
          </Form.Group>
          <Form.Group controlId="email-test-ad" style={{ width: 160 }}>
            <Form.Label>Email ad</Form.Label>
            <Form.Control
              inputMode="numeric"
              value={adId}
              placeholder="e.g. 95952"
              onChange={(e) => setAdId(e.target.value.replace(/\D/g, ""))}
            />
            <Form.Text className="text-muted">The adId from the Content Catalog URL.</Form.Text>
          </Form.Group>
          {!fromAd && (
            <Form.Group controlId="email-test-subject" style={{ minWidth: 280 }}>
              <Form.Label>Subject</Form.Label>
              <Form.Control value={subject} onChange={(e) => setSubject(e.target.value)} />
            </Form.Group>
          )}
          <Form.Group controlId="email-test-sender" style={{ minWidth: 200 }}>
            <Form.Label>Institution name</Form.Label>
            <Form.Control
              value={senderName}
              placeholder="Jefferson CC"
              onChange={(e) => setSenderName(e.target.value)}
            />
            <Form.Text className="text-muted">Named in the footer, as CAN-SPAM wants.</Form.Text>
          </Form.Group>
        </div>

        {!fromAd && (
          <Form.Group controlId="email-test-body" className="mb-3">
            <Form.Label>Message</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Hi {{first_name}}, registration closes on the 14th."
            />
            <Form.Text className="text-muted">
              {"{{first_name}}"} and {"{{last_name}}"} are filled in per recipient. A test send
              has no recipient record, so they come through as typed. The unsubscribe footer is
              added automatically and cannot be removed.
            </Form.Text>
          </Form.Group>
        )}

        <div className="mb-3">
          <Button onClick={send} disabled={
              sending || recipients.length === 0 || (!fromAd && (!subject || !body.trim()))
            }>
            {sending ? <Spinner size="sm" animation="border" /> : "Send test email"}
          </Button>
        </div>

        {errors.map((message) => (
          <Alert key={message} variant="danger" className="mb-2">
            {message}
          </Alert>
        ))}

        {results.map((result) => (
          <div key={result.message_id} className="mt-2">
            <Badge bg="success">Sent</Badge>{" "}
            <span className="text-muted small">
              &ldquo;{result.subject}&rdquo; to {result.to}. Check the footer and click the
              unsubscribe link to confirm it works before a real campaign relies on it.
            </span>
            <div className="text-muted mt-1" style={{ fontSize: 11 }}>
              Message {result.message_id}
            </div>
          </div>
        ))}
      </Card.Body>
    </Card>
  );
};
